import { useMemo } from 'react';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { CADI_DROUGHT_LEGEND, CADI_FILL_NODATA, cadiCategoryFromMean, getCadiAlertsFrames } from '../constants/cadiDroughtLayers';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CADITimeseriesPoint {
  date: string;
  label: string;
  mean: number;
  min: number;
  max: number;
  validPercent: number;
}

export type FarmCADITimeseriesState =
  | { status: 'idle' }
  | { status: 'loading'; progress: number; total: number }
  | { status: 'loaded'; points: CADITimeseriesPoint[] }
  | { status: 'unavailable' }   // demo / unsaved field — no CADI pixel coverage (hide silently)
  | { status: 'no_coverage'; message: string }  // saved field — show friendly explanation
  | { status: 'error'; message: string };

// ── Fetch helper (called from AnalysisPanel, not from inside this component) ──

async function fetchCADIStatForCog(
  cogUrl: string,
  bidx: number,
  featureBody: Record<string, unknown>,
  signal: AbortSignal
): Promise<{ mean: number; min: number; max: number; validPercent: number } | null> {
  const params = new URLSearchParams({ url: cogUrl, bidx: String(bidx), nodata: CADI_FILL_NODATA });
  const endpoint = `${DIGISAKA_API_ORIGIN}/api/statistics?${params.toString()}`;

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(featureBody),
    signal,
  });

  if (!res.ok) return null;

  const data = await res.json();
  // TiTiler POST /statistics returns a FeatureCollection; stats keyed by requested band.
  const featProps = data?.features?.[0]?.properties ?? data?.properties;
  const stats =
    featProps?.statistics?.[`b${bidx}`] ??
    featProps?.statistics?.b1 ??
    featProps?.statistics?.[String(bidx)] ??
    featProps?.statistics?.['1'];
  if (!stats) return null;
  if (stats.mean == null) return null;
  if ((stats.valid_pixels ?? 0) <= 0 || (stats.valid_percent ?? 0) <= 0) return null;
  if (stats.mean < 0 || stats.mean > 1) return null;

  return {
    mean: stats.mean ?? 0,
    min: stats.min ?? 0,
    max: stats.max ?? 0,
    validPercent: stats.valid_percent ?? 0,
  };
}

/**
 * Fetch CADI timeseries stats for the Alerts-tab horizon (current month + next months)
 * clipped to the given farm polygon.
 * Call this from AnalysisPanel (not from inside FarmCADITimeseriesChart) so data survives
 * tab switches without re-fetching.
 *
 * @param fieldGeoJson  The farm's GeoJSON Feature or raw Geometry.
 * @param onProgress    Called after each COG finishes loading (progress, total).
 * @param signal        AbortSignal to cancel in-flight requests.
 */
export async function fetchCADITimeseries(
  fieldGeoJson: Record<string, unknown>,
  onProgress: (progress: number, total: number) => void,
  signal: AbortSignal
): Promise<CADITimeseriesPoint[]> {
  // Unwrap Feature → bare Geometry if needed
  const rawGeometry =
    (fieldGeoJson as Record<string, unknown>)?.type === 'Feature'
      ? (fieldGeoJson as Record<string, unknown>).geometry
      : fieldGeoJson;

  // TiTiler POST /api/statistics requires a FeatureCollection body
  const featureBody = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry: rawGeometry, properties: {} }],
  };

  const frames = getCadiAlertsFrames(1);
  let completed = 0;
  const total = frames.length;

  if (total === 0) {
    onProgress(0, 0);
    return [];
  }

  const results = await Promise.all(
    frames.map(async (frame) => {
      try {
        const stat = await fetchCADIStatForCog(frame.cogUrl, frame.bidx, featureBody, signal);
        completed += 1;
        onProgress(completed, total);
        return stat ? { date: frame.date, label: frame.label, ...stat } : null;
      } catch {
        completed += 1;
        onProgress(completed, total);
        return null;
      }
    })
  );

  return frames
    .map((frame, i) => {
      const r = results[i];
      return r
        ? { date: frame.date, label: frame.label, mean: r.mean, min: r.min, max: r.max, validPercent: r.validPercent }
        : { date: frame.date, label: frame.label, mean: NaN, min: NaN, max: NaN, validPercent: 0 };
    })
    .filter((p) => !isNaN(p.mean));
}

// ── Pure display helpers ───────────────────────────────────────────────────────

const CADI_LABEL_STYLES: Record<string, { bgClass: string; textClass: string; darkBgClass: string; darkTextClass: string }> = {
  'No drought': {
    bgClass: 'bg-emerald-50 border-emerald-200',
    textClass: 'text-emerald-800',
    darkBgClass: 'bg-emerald-950/60 border-emerald-700/50',
    darkTextClass: 'text-emerald-300',
  },
  Dry: {
    bgClass: 'bg-yellow-50 border-yellow-200',
    textClass: 'text-yellow-800',
    darkBgClass: 'bg-yellow-950/60 border-yellow-700/50',
    darkTextClass: 'text-yellow-200',
  },
  Moderate: {
    bgClass: 'bg-amber-50 border-amber-300',
    textClass: 'text-amber-800',
    darkBgClass: 'bg-amber-950/60 border-amber-700/50',
    darkTextClass: 'text-amber-300',
  },
  Severe: {
    bgClass: 'bg-orange-50 border-orange-300',
    textClass: 'text-orange-800',
    darkBgClass: 'bg-orange-950/60 border-orange-700/50',
    darkTextClass: 'text-orange-300',
  },
  Extreme: {
    bgClass: 'bg-red-50 border-red-300',
    textClass: 'text-red-800',
    darkBgClass: 'bg-red-950/60 border-red-700/50',
    darkTextClass: 'text-red-300',
  },
  Exceptional: {
    bgClass: 'bg-rose-50 border-rose-400',
    textClass: 'text-rose-900',
    darkBgClass: 'bg-rose-950/60 border-rose-700/50',
    darkTextClass: 'text-rose-300',
  },
};

function cadiDroughtLabel(value: number, dark = false) {
  const cat = cadiCategoryFromMean(value);
  const style = CADI_LABEL_STYLES[cat.label] ?? CADI_LABEL_STYLES.Exceptional;
  return {
    label: cat.label,
    color: cat.color,
    bgClass: dark ? style.darkBgClass : style.bgClass,
    textClass: dark ? style.darkTextClass : style.textClass,
  };
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ── Pure display component ────────────────────────────────────────────────────

type FarmCADITimeseriesChartProps = {
  /** State managed by the parent (AnalysisPanel). No fetching happens inside this component. */
  cadiState: FarmCADITimeseriesState;
  compact?: boolean;
};

export default function FarmCADITimeseriesChart({ cadiState, compact = false }: FarmCADITimeseriesChartProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const nowKey = useMemo(() => currentMonthKey(), []);

  const chart = useMemo(() => {
    if (cadiState.status !== 'loaded') return null;
    const { points } = cadiState;
    if (points.length === 0) return null;

    const width = compact ? 280 : 340;
    const height = compact ? 120 : 140;
    const padL = 36;
    const padR = 16;
    const padT = 16;
    const padB = 42;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;

    const coords = points.map((p, i) => {
      const x = padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
      const y = padT + innerH - p.mean * innerH; // CADI already 0–1
      return { x, y, ...p };
    });

    const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
    const areaPath =
      `${linePath} ` +
      `L${coords[coords.length - 1].x.toFixed(1)},${(padT + innerH).toFixed(1)} ` +
      `L${coords[0].x.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

    const strokeBase = isDark ? '#fb923c' : '#d97706';
    const yRefLines = [0.2, 0.4, 0.6, 0.8].map((v) => ({
      y: padT + innerH - v * innerH,
      label: v.toFixed(1),
    }));

    return { width, height, coords, linePath, areaPath, strokeBase, yRefLines, padL, padR, padT, innerH };
  }, [cadiState, compact, isDark]);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (cadiState.status === 'idle' || cadiState.status === 'unavailable') return null;

  if (cadiState.status === 'no_coverage') {
    return (
      <div className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2.5' : 'p-3.5'}`}>
        <div className={`mb-2 flex items-center gap-2 ${ap.muted} ${compact ? 'text-[10px]' : 'text-xs'}`}>
          <i className={`ri-bar-chart-2-line ${compact ? 'text-sm' : 'text-base'} shrink-0`} aria-hidden />
          <span className="font-semibold">CADI Drought Timeseries</span>
        </div>
        <div className={`flex items-start gap-2 ${compact ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
          <i className="ri-information-line shrink-0 mt-0.5 text-amber-600" aria-hidden />
          <p>{cadiState.message}</p>
        </div>
      </div>
    );
  }

  if (cadiState.status === 'loading') {
    const pct = cadiState.total > 0 ? Math.round((cadiState.progress / cadiState.total) * 100) : 0;
    return (
      <div className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2.5' : 'p-3.5'}`}>
        <div className={`mb-2 flex items-center gap-2 ${ap.muted} ${compact ? 'text-[10px]' : 'text-xs'}`}>
          <i className={`ri-bar-chart-2-line ${compact ? 'text-sm' : 'text-base'} shrink-0`} aria-hidden />
          <span className="font-semibold">CADI Drought Timeseries</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-full rounded-full bg-amber-500 transition-all duration-200" style={{ width: `${pct}%` }} />
          </div>
          <span className={`shrink-0 tabular-nums ${compact ? 'text-[9px]' : 'text-[10px]'} ${ap.faint}`}>
            {cadiState.progress}/{cadiState.total}
          </span>
        </div>
        <p className={`mt-1 ${compact ? 'text-[9px]' : 'text-[10px]'} ${ap.faint}`}>
          Clipping CADI rasters to farm boundary…
        </p>
      </div>
    );
  }

  if (cadiState.status === 'error') {
    return (
      <div className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2.5' : 'p-3.5'}`}>
        <div className={`flex items-center gap-2 ${compact ? 'text-[10px]' : 'text-xs'} text-red-500`}>
          <i className="ri-error-warning-line shrink-0" aria-hidden />
          <span>{cadiState.message}</span>
        </div>
      </div>
    );
  }

  if (!chart || cadiState.status !== 'loaded') return null;

  const { points } = cadiState;
  const currentPoint = points.find((p) => p.date === nowKey);
  const highlightPoint = currentPoint ?? points[points.length - 1];
  const dl = cadiDroughtLabel(highlightPoint?.mean ?? 0, isDark);

  return (
    <div className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2.5' : 'p-3.5'}`}>
      {/* Header */}
      <div className={`mb-2 flex items-center justify-between gap-2 ${compact ? 'text-[10px]' : 'text-xs'}`}>
        <div className="flex items-center gap-1.5">
          <i className={`ri-bar-chart-2-line shrink-0 ${compact ? 'text-sm' : 'text-base'}`}
            style={{ color: isDark ? '#fb923c' : '#b45309' }} aria-hidden />
          <span className={`font-semibold ${ap.heading}`}>CADI Drought Timeseries</span>
        </div>
        <span className={`shrink-0 ${ap.faint}`}>Next 4 months</span>
      </div>

      {/* Current status badge */}
      {highlightPoint && (
        <div className={`mb-2.5 flex flex-wrap items-center gap-1.5 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${dl.bgClass} ${dl.textClass}`}>
            {dl.label}
          </span>
          <span className={ap.faint}>{highlightPoint.label} — CADI: {highlightPoint.mean.toFixed(3)}</span>
        </div>
      )}

      {/* SVG chart */}
      <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full max-w-full"
        role="img" aria-label="CADI drought timeseries chart for selected farm">
        {/* Y-axis reference grid lines */}
        {chart.yRefLines.map(({ y, label }) => (
          <g key={label}>
            <line x1={chart.padL} y1={y} x2={chart.width - chart.padR} y2={y}
              stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="1" strokeDasharray="3,3" />
            <text x={chart.padL - 3} y={y + 3} textAnchor="end" fontSize={compact ? 7 : 8}
              fill={isDark ? '#6b7280' : '#9ca3af'}>{label}</text>
          </g>
        ))}

        {/* Baseline */}
        <line x1={chart.padL} y1={chart.padT + chart.innerH} x2={chart.width - chart.padR} y2={chart.padT + chart.innerH}
          stroke={isDark ? '#4b5563' : '#d1d5db'} strokeWidth="1" />

        <path d={chart.areaPath} fill={isDark ? `${chart.strokeBase}22` : `${chart.strokeBase}18`} />
        <path d={chart.linePath} fill="none" stroke={chart.strokeBase} strokeWidth="2" strokeLinejoin="round" />

        {/* El Niño annotation banner */}
        <rect
          x={chart.padL} y={chart.padT}
          width={chart.width - chart.padL - chart.padR}
          height={chart.innerH}
          fill={isDark ? 'rgba(251,146,60,0.04)' : 'rgba(251,146,60,0.06)'}
          rx="3"
        />
        <text
          x={chart.width - chart.padR - 3}
          y={chart.padT + 9}
          textAnchor="end"
          fontSize={compact ? 7 : 7.5}
          fontWeight="600"
          fill={isDark ? '#fb923c' : '#b45309'}
          opacity="0.85"
        >
          ⚠ El Niño period
        </text>

        {/* Data points — each dot filled with its drought-severity color */}
        {chart.coords.map((c, i) => {
          const isNow = c.date === nowKey;
          const dotColor = cadiDroughtLabel(c.mean).color;
          const showValueLabel = isNow || i === 0 || i === chart.coords.length - 1;

          return (
            <g key={c.date}>
              {isNow && (
                <circle cx={c.x} cy={c.y} r={compact ? 6 : 7} fill="none"
                  stroke={dotColor} strokeWidth="1.5" opacity="0.5" />
              )}
              {/* All dots colored by severity; current is larger and fully opaque */}
              <circle cx={c.x} cy={c.y} r={isNow ? (compact ? 4 : 4.5) : compact ? 2.5 : 3}
                fill={dotColor}
                opacity={isNow ? 1 : 0.75}
                stroke={isDark ? '#111827' : '#fff'}
                strokeWidth={isNow ? 0 : 1} />
              <text
                x={c.x}
                y={chart.padT + chart.innerH + 6}
                textAnchor="middle"
                fontSize={compact ? 7 : 8}
                fill={isNow ? dotColor : isDark ? '#9ca3af' : '#6b7280'}
                fontWeight={isNow ? '700' : '400'}
              >
                {c.label}
              </text>
              {showValueLabel && (
                <text x={c.x} y={c.y - 6} textAnchor="middle" fontSize={compact ? 7 : 8}
                  fontWeight={isNow ? '700' : '500'}
                  fill={isNow ? dotColor : isDark ? '#d1d5db' : '#374151'}>
                  {c.mean.toFixed(2)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend strip */}
      <div className={`mt-2 flex items-center gap-1.5 ${compact ? 'text-[8px]' : 'text-[9px]'} ${ap.faint}`}>
        <div className="flex gap-0.5">
          {CADI_DROUGHT_LEGEND.colors.map((color, i) => (
            <div key={i} className="h-2 w-4 rounded-sm" style={{ backgroundColor: color }}
              title={CADI_DROUGHT_LEGEND.labels[i]} />
          ))}
        </div>
        <span>0 = no drought → 1 = exceptional</span>
      </div>
    </div>
  );
}
